import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Center, Spinner, VStack, Text } from '@chakra-ui/react';
import { supabase } from '../lib/supabase';
import { useCourse } from '../contexts/CourseContext';
import { useCourseConfig } from '../hooks/useCourseConfig';
import CourseLandingPage from '../pages/course/CourseLandingPage';

// Computes the next step slug based on DB state.
// MVP logic:
// - If 'landing' exists and consent_given is not true -> go to 'landing'
// - Else if 'preferences' exists and no learning_preference/name saved -> go to 'preferences'
// - Else if 'activity' exists and required activities not completed -> go to 'activity'
// - Else if 'chat' exists -> go to 'chat'
// - Else -> go to the first step in the flow
async function computeNextSlug(courseId: string, flowSlugs: string[]): Promise<string> {
  const defaultNext = flowSlugs[0] || 'landing';

  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return defaultNext;

    // Pull consent and preference fields for this user/course
    const { data, error } = await supabase
      .from('course_learner_preferences')
      .select('consent_given, learning_preference, course_preferred_name')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .maybeSingle();

    if (error && (error as any).code !== 'PGRST116') {
      // Real error other than "no rows"
      console.warn('[CourseFlowRouter] Error fetching preferences:', error);
      return defaultNext;
    }

    const consentGiven = Boolean(data?.consent_given);
    // Align with UI rule: preferences are considered complete only if learning_preference has >= 10 chars
    const lpValue = (data?.learning_preference ?? '') as any;
    // Handle string or JSONB payloads defensively
    const lpText = typeof lpValue === 'string' 
      ? lpValue 
      : (lpValue == null ? '' : JSON.stringify(lpValue));
    const hasPrefs = lpText.trim().length >= 10;

    // Check required activities
    let hasCompletedRequiredActivities = true;
    if (flowSlugs.includes('activity')) {
      // Get course config to check for required activities
      const { data: courseData } = await supabase
        .from('courses')
        .select('code')
        .eq('id', courseId)
        .single();

      if (courseData) {
        // Import course registry dynamically to check for activities
        const { courseRegistry } = await import('../courses/registry');
        const courseConfig = courseRegistry[courseData.code as keyof typeof courseRegistry];
        
        let requiredActivityKeys: string[] = [];
        
        // Check for explicit activity requirements
        if (courseConfig?.activities?.requirements) {
          requiredActivityKeys = courseConfig.activities.requirements.map(req => req.activity_key);
        }
        
        // Check for autoStartWithActivity (treat as required)
        if (courseConfig?.chat?.autoStartWithActivity && !requiredActivityKeys.includes(courseConfig.chat.autoStartWithActivity)) {
          requiredActivityKeys.push(courseConfig.chat.autoStartWithActivity);
        }
        
        // Check if user has completed all required activities
        if (requiredActivityKeys.length > 0) {
          // Get activity IDs for the required activity keys
          const { data: activities, error: activitiesError } = await supabase
            .from('learning_activities')
            .select('id')
            .in('activity_key', requiredActivityKeys);

          if (!activitiesError && activities) {
            const activityIds = activities.map(a => a.id);
            
            const { data: responses, error: responsesError } = await supabase
              .from('learner_responses')
              .select('activity_id')
              .eq('user_id', user.id)
              .eq('course_id', courseId)
              .in('activity_id', activityIds);

            if (!responsesError && responses) {
              hasCompletedRequiredActivities = responses.length >= requiredActivityKeys.length;
            } else {
              hasCompletedRequiredActivities = false;
            }
          }
        }
      }
    }

    if (flowSlugs.includes('landing') && !consentGiven) {
    return 'landing';
  }
    if (flowSlugs.includes('preferences') && !hasPrefs) {
      return 'preferences';
    }
    // Activity should only be available if consent is given
    if (flowSlugs.includes('activity') && consentGiven && !hasCompletedRequiredActivities) return 'activity';
    if (flowSlugs.includes('chat')) return 'chat';

    return defaultNext;
  } catch (e) {
    console.warn('[CourseFlowRouter] Fallback to default due to error:', e);
    return defaultNext;
  }
}

export default function CourseFlowRouter() {
  const navigate = useNavigate();
  const { currentCourse, isLoading } = useCourse();
  const config = useCourseConfig();

  // Extract flow slugs to avoid complex expression in dependency array
  const flowSlugsKey = config.flow.map(s => s.slug).join(':');

  useEffect(() => {
    let cancelled = false;

    async function resolveAndNavigate() {
      if (isLoading) return; // wait for course
      if (!currentCourse) return; // handled below by rendering fallback UI

      const flowSlugs = config.flow.map(s => s.slug);
      const next = await computeNextSlug(currentCourse.id, flowSlugs);
      if (!cancelled) {
        navigate(`/s/${next}`, { replace: true });
      }
    }

    resolveAndNavigate();
    return () => { cancelled = true; };
  }, [currentCourse, isLoading, flowSlugsKey, navigate, config.flow]);

  // Loading UI while deciding where to go
  if (isLoading) {
    return (
      <Center height="60vh">
        <VStack spacing={3}>
          <Spinner size="xl" />
          <Text>Preparing your course flow…</Text>
        </VStack>
      </Center>
    );
  }

  // If no course is assigned, reuse the existing landing page fallback UI
  if (!currentCourse) {
    return <CourseLandingPage />;
  }

  // While effect navigates, show a spinner briefly
  return (
    <Center height="60vh">
      <Spinner size="xl" />
    </Center>
  );
}
